import * as _ from "lodash";

import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";
import { CustomerCartMetaStore } from "shared/src/uidispatcherapi/CustomerCartMetaStore";
import simplesocket from "shared/src/uidispatcherapi/SimpleSocket";
import * as CustomerCartClient from "../clients/CustomerCartClient";
import { Store } from "shared/src/stores/Store";
import { generateUuid } from "shared/src/utils/util";
import { timeout } from "../utils/timeout";
import { buildPath } from "shared/src/utils/RequestUtil";
import { convertFromAnonCartItem } from "shared/src/utils/ShoppingCartPriceDataConverter";
import { CustomerCartItemPrices } from "shared/src/types/CartPrice";

type BettyBundleId = string;
type CustomerCart = CustomerCartClient.CustomerCart;
import { ShoppingCartPosition } from "shared/src/types/ShoppingCartPosition";
import { notify } from "shared/src/utils/Notifications";
import TrackingUtil from "shared/src/utils/TrackingUtil";
import { AddItemBatchRequest } from "../clients/CustomerCartClient";
import { BundleWithQuantity } from "../utils/ListBulkSelectionUtils";
import Tracking from "shared/src/uidispatcherapi/Tracking";
import { isTier12Customer } from "shared/src/stores/SessionStore";

type CartItemChangeState = "pending" | "sending" | "sent"; // sent and 200 received, but that doesn't mean it's contained in the cart yet

type CartItemChange = {
    // we only send "set amount to x" changes, which simplifies a lot
    state: CartItemChangeState;
    requestId: string;
    bundleId: BettyBundleId;
    amount: number;
};

export class CustomerCartStoreImpl extends Store {
    _wsConnection:
        | {
              close: () => void;
          }
        | null
        | undefined;
    _wsCartId: string | null | undefined = null;
    _pollingCart = false;
    _sendingChanges = false;
    _cartId: string | null | undefined = null;
    // full cart received from server + events from websocket we could apply ourselves
    _fullCart: CustomerCart | null = null;
    // ids of all confirmed events
    _confirmedEvents: Set<string> = new Set();
    // changes sent to the server already
    _changes: Array<CartItemChange> = [];
    // predictedAmounts = _fullCart + _changes
    _predictedAmounts: {
        [x: string]: number;
    } = {};
    _currentPromotionIds: string[] | null = null;

    constructor() {
        super();
        this.listenTo(CustomerCartMetaStore, this.connect);
        // make sure we connect when the SessionStore isn't pending anymore
        this.listenTo(SessionStore, this.connect);
        this.connect();
    }

    // for testing
    _reset() {
        this._wsConnection = null;
        this._wsCartId = null;
        this._pollingCart = false;
        this._cartId = null;
        this._fullCart = null;
        this._confirmedEvents = new Set();
        this._changes = [];
        this._predictedAmounts = {};
        this._sendingChanges = false;
        this._currentPromotionIds = null;
    }

    connect() {
        const newCartId = CustomerCartMetaStore.getCartId();
        if (newCartId) {
            if (newCartId !== this._cartId) {
                this._confirmedEvents = new Set();
            }
            this._cartId = newCartId;
            this._connectSocket();
        }
    }

    _connectSocket() {
        const cartId = this._cartId;
        const customerId = SessionStore.getCustomerId();
        const storeId = SessionStore.getSelectedStoreId();
        if (
            this._wsCartId === cartId ||
            !cartId ||
            !customerId ||
            !storeId ||
            !SessionStore.loggedIn() ||
            isTier12Customer()
        ) {
            return;
        }
        if (this._wsConnection) {
            this._wsConnection.close();
        }
        this._wsCartId = cartId;
        const url = buildPath(["oc", "customer-carts", "events", customerId, storeId, cartId]);
        this._wsConnection = simplesocket.initConnection({
            channel: "/" + url,
            onOpenFailed: () => {
                Tracking.trackError("searchdiscover", "ui", "CustomerCartStore", "Opening websocket failed");
                this._wsConnection = null;
                this._pollCart();
            },
            onClose: () => {
                this._wsConnection = null;
                this._pollCart();
            },
            onMessage: (json: any) => this._handleCartEvent(json)
        });
        // it would be more correct to do this after the socket is opened, but simplesocket only calls onOpen for the
        // first subscriber to a socket
        this._reloadFullCart();
    }

    async _reloadFullCart() {
        // reload the cart, but try checking the version first
        if (!this._cartId) {
            return;
        }

        let newVersion;
        try {
            const response = await CustomerCartClient.getCartVersion({
                cartId: this._cartId,
                cartContext: this._getCartContext()
            });
            newVersion = response && response.cartVersion;
        } catch (e) {
            console.warn("Cart polling error:", e);
        }

        if (
            this._cartId &&
            (!this._fullCart ||
                this._fullCart.cartId !== this._cartId ||
                (newVersion && newVersion !== this._fullCart.cartVersion))
        ) {
            let newCart;
            try {
                newCart = await CustomerCartClient.getCart({
                    cartId: this._cartId,
                    cartContext: this._getCartContext()
                });

                this._handleFullCartUpdate(newCart);
            } catch (e: any) {
                Tracking.trackError("searchdiscover", "ui", "CustomerCartStore", "Error retrieving new cart:", e);
            }
        }
    }

    async _pollCart() {
        if (this._pollingCart || !this._cartId) {
            return;
        }
        this._pollingCart = true;
        try {
            while (true) {
                if (!this._cartId || this._wsConnection) {
                    return;
                }

                await this._reloadFullCart();

                await timeout(2000);
            }
        } finally {
            this._pollingCart = false;
        }
    }

    _handleFullCartUpdate(cart: CustomerCartClient.CustomerCart | undefined | null) {
        if (!cart) {
            return;
        }
        this._fullCart = cart;
        cart.eventHistory.forEach((e) => this._confirmedEvents.add(e));
        this._removeConfirmedChanges();
        this._updatePredictedAmounts();
        this.trigger();
    }

    _handleCartEvent(event: CustomerCartClient.CartEvent) {
        // this event application logic is obviously very incomplete, it only covers the data we're interested in
        // (i.e. what's defined in the CustomerCart type)
        const { bundleId, quantity } = event.payload;
        if (event.status.type === "Success") {
            switch (event.type) {
                case "ItemAddedEvent":
                case "ItemQuantitySetEvent": {
                    if (bundleId && typeof quantity === "number" && this._fullCart) {
                        // Update cart version
                        this._fullCart.cartVersion = event.seqNum;

                        // Update prices for all items provided in the event (if any)
                        if (event.prices && event.prices.articlePrices) {
                            Object.entries(event.prices.articlePrices).forEach(([id, priceData]) => {
                                if (this._fullCart!.items[id]) {
                                    this._fullCart!.items[id].prices = priceData;
                                }
                            });
                        }

                        // Ensure the targeted item has the correct quantity.
                        // Use its existing prices if available, or try to pick from the event.
                        this._fullCart.items[bundleId] = {
                            bundleId: bundleId,
                            quantity: quantity,
                            prices: this._fullCart.items[bundleId]
                                ? this._fullCart.items[bundleId].prices
                                : event.prices && event.prices.articlePrices[bundleId],
                            errors: []
                        };
                    }
                    break;
                }
                case "ItemRemovedEvent": {
                    if (bundleId && this._fullCart) {
                        this._fullCart.cartVersion = event.seqNum;
                        // Remove the item
                        delete this._fullCart.items[bundleId];
                        // Also update prices for other items if provided
                        if (event.prices && event.prices.articlePrices) {
                            Object.entries(event.prices.articlePrices).forEach(([id, priceData]) => {
                                if (this._fullCart!.items[id]) {
                                    this._fullCart!.items[id].prices = priceData;
                                }
                            });
                        }
                    }
                    break;
                }
                case "CartCreatedEvent":
                    this._reloadFullCart();
                    return;
                case "CartSavedEvent":
                case "ItemServiceOptionsSetEvent":
                case "ItemCommentSetEvent":
                case "ItemManualPriceSetEvent":
                case "CheckoutCreatedEvent":
                    // we don't care about events which do not modify item quantities
                    return;
                case "CartDeletedEvent":
                    // this will cause OC to provide a new cart id
                    return;
                default: {
                    console.log("Unknown event:", event.type, "reloading cart");
                    this._reloadFullCart();
                    // we don't want to immediately update our prediction in this case
                    return;
                }
            }
        } else if (event.status.type === "Failure") {
            // we don't really care, OC will show an error message
        }
        this._confirmedEvents.add(event.eventId);
        this._removeConfirmedChanges();
        this._updatePredictedAmounts();

        this.trigger();
    }

    _removeConfirmedChanges() {
        _.remove(this._changes, (change) => this._confirmedEvents.has(change.requestId));
    }

    _updatePredictedAmounts() {
        const amounts = _.mapValues(
            this._fullCart === null ? {} : this._fullCart.items,
            (item: CustomerCartClient.CustomerCartItem) => item.quantity
        );
        for (const change of this._changes) {
            amounts[change.bundleId] = change.amount;
        }
        this._predictedAmounts = amounts;
    }

    _getCartContext(): CustomerCartClient.CartContext {
        return {
            country: SessionStore.getCountry(),
            locale: SessionStore.getLocale(),
            // if these change, we rely on the CustomerCartMetaStore to change the cartId:
            storeId: SessionStore.getSelectedStoreId(),
            customerId: SessionStore.getCustomerId(),
            cardholderNumber: SessionStore.getCardholderNumber() || "",
            fsdAddressId: SessionStore.getSelectedDeliveryAddressId()
        };
    }

    async _sendChanges() {
        if (this._sendingChanges) {
            return;
        }
        try {
            this._sendingChanges = true;
            let errorWait = 500; // milliseconds
            while (true) {
                let change;
                const bundlesInCart = new Set(_.keys(this._fullCart ? this._fullCart.items : {}));
                // we need to keep track of what's in the cart so we know whether to do addItem or setQuantity
                for (const c of this._changes) {
                    if (c.state === "sent") {
                        bundlesInCart.add(c.bundleId);
                    } else {
                        // found one to send
                        change = c;
                        break;
                    }
                }
                if (!change) {
                    return;
                }
                change.state = "sending";

                if (!this._cartId) {
                    return;
                }

                const args = {
                    requestId: change.requestId,
                    cartId: this._cartId,
                    cartContext: this._getCartContext(),
                    bundleId: change.bundleId,
                    quantity: change.amount
                };
                try {
                    if (bundlesInCart.has(change.bundleId)) {
                        await CustomerCartClient.setItemQuantity(args);
                    } else {
                        await CustomerCartClient.addItem(args);
                    }
                    change.state = "sent";
                    errorWait = 500; // reset after successful request
                    await timeout(1000); // wait a bit before the next change
                } catch (e: any) {
                    if (errorWait < 10000) {
                        console.warn("Error sending change:", e);
                        // wait a while
                        await timeout(errorWait);
                        errorWait *= 2;
                    } else {
                        // already tried too often
                        Tracking.trackError("searchdiscover", "ui", "CustomerCartStore", "Error sending change:", e);
                        const notification = {
                            type: "warning" as const,
                            key: "EX_ADD_TO_BASKET_FAILED",
                            values: [],
                            id: "EX_ADD_TO_BASKET_FAILED"
                        };
                        notify(notification);
                        _.pull(this._changes, change);
                        this._updatePredictedAmounts();
                        this.trigger();
                        errorWait = 500;
                    }
                }
            }
        } finally {
            this._sendingChanges = false;
        }
    }

    _generateRequestId(): string {
        return "BTEX-" + generateUuid();
    }

    handleSetItemInCart(bundleId: BettyBundleId, amount: number) {
        const cartId = this._cartId;
        if (!cartId) {
            return;
        }
        const requestId = this._generateRequestId();
        const change = {
            state: "pending" as const,
            requestId,
            bundleId,
            amount
        };
        // remove preceding pending changes to the same item
        _.remove(this._changes, (change) => change.bundleId === bundleId && change.state === "pending");
        this._changes.push(change);
        this._updatePredictedAmounts();
        this._sendChanges();
        this.trigger();
    }

    handleChangeAmountInCart(bettyBundleId: BettyBundleId, byAmount: number) {
        const amount = this.getIncreasedQuantity(bettyBundleId, byAmount);
        this.handleSetItemInCart(bettyBundleId, amount);
    }

    getIncreasedQuantity(bettyBundleId: BettyBundleId, byAmount: number): number {
        return _.round(byAmount + (this._predictedAmounts[bettyBundleId] || 0), 2);
    }

    handleBulkAddToBasket(bundlesWithQuantity: BundleWithQuantity[]): void {
        this._reloadFullCart().then(() => {
            const request = this.createBatchRequestBody(bundlesWithQuantity);
            if (!request) {
                Tracking.trackError(
                    "searchdiscover",
                    "ui",
                    "CustomerCartStore",
                    "Could not create request body for bundles ",
                    bundlesWithQuantity.map((e) => e.bundleId)
                );
                return;
            }
            this._updatePredictedAmounts();
            CustomerCartClient.addItemBatch(request)
                .then(() => {
                    this.trigger();
                })
                .catch((error) => {
                    const notification = {
                        type: "warning" as const,
                        key: "EX_ADD_TO_BASKET_FAILED",
                        values: [],
                        id: "EX_ADD_TO_BASKET_FAILED",
                        timeout: 5000
                    };
                    notify(notification);
                    const trackData: { [bundleId: string]: string } = {};
                    bundlesWithQuantity
                        .map((e) => e.bundleId)
                        .forEach((id) => {
                            trackData[id] = id;
                        });
                    TrackingUtil.trackUiErrorSd("CustomerCartStory", "bulk add to basket failed.", error, trackData);
                });
        });
    }

    createBatchRequestBody(bundlesWithQuantity: BundleWithQuantity[]): AddItemBatchRequest | null {
        const cartId = CustomerCartMetaStore.getCartId();
        if (cartId) {
            return {
                cartContext: CustomerCartClient.createCartContext(),
                cartId: cartId,
                requestId: "BTCA-" + generateUuid(),
                type: "BATCH",
                items: _.reduce(
                    bundlesWithQuantity,
                    (result, bundleWithQuantity, index: number) => {
                        const id = bundleWithQuantity.bundleId;
                        const quantity = this.getIncreasedQuantity(id, bundleWithQuantity.quantity);
                        result[id] = {
                            bundleId: bundleWithQuantity.basketBundleId,
                            quantity: quantity,
                            comment: "",
                            sortingIndex: index
                        };
                        return result;
                    },
                    {} as { [bundleId: string]: any }
                )
            };
        }
        return null;
    }

    getItemCount(): number {
        return _.size(this._predictedAmounts);
    }

    getItemAmount(bettyBundleId: BettyBundleId): number | null {
        return bettyBundleId in this._predictedAmounts ? this._predictedAmounts[bettyBundleId] : null;
    }

    // FIXME(FD) rename to getCartItem
    getCartItemTotalNetPrice(bettyBundleId: BettyBundleId): ShoppingCartPosition | null {
        const cart = this._fullCart;
        if (!cart) {
            return null;
        }
        const item = cart.items[bettyBundleId];
        if (!item) {
            return {
                loading: bettyBundleId in this._predictedAmounts,
                prices: null
            };
        } else {
            return {
                loading: item.quantity !== this._predictedAmounts[bettyBundleId],
                prices: convertFromAnonCartItem(item.prices)
            };
        }
    }

    getAllCartItems(): CustomerCartClient.CustomerCartItem[] {
        return this._fullCart?.items ? Object.values(this._fullCart.items) : [];
    }

    isEmpty(): boolean {
        return _.isEmpty(this._predictedAmounts);
    }

    getCartCurrentDnrNumbers(): string[] {
        if (this._fullCart) {
            const allCartItems = this._fullCart.items;
            const allDnrNumbers = new Set<string>();

            Object.values(allCartItems).forEach((itemInCart) => {
                if (itemInCart?.prices?.appliedDnrs && Array.isArray(itemInCart.prices.appliedDnrs)) {
                    itemInCart.prices.appliedDnrs.forEach((dnr) => {
                        if (dnr.number) {
                            allDnrNumbers.add(dnr.number);
                        }
                    });
                }
            });

            return Array.from(allDnrNumbers);
        }
        return [];
    }

    getPricesWithDnrInCart(): CustomerCartItemPrices[] | null {
        const cart = this._fullCart;
        if (!cart) {
            return null;
        }

        // Extract all appliedDnrs and flatten the array
        const dnrItems = Object.values(cart.items)
            .flatMap((item) => item.prices || [])
            .filter((prices) => prices.appliedDnrs && prices.appliedDnrs.length > 0);

        return dnrItems;
    }
}

export const CustomerCartStore = new CustomerCartStoreImpl();
